import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface VideoSliderProps {
  videoSizeRatio: number;
  onValueChange: (value: number) => void;
  videoAreaRef: React.RefObject<HTMLDivElement>;
}

/**
 * Auto-hiding video size slider with mouse interaction
 * Automatically shows/hides based on mouse position
 */
export const VideoSlider: React.FC<VideoSliderProps> = ({
  videoSizeRatio,
  onValueChange,
  videoAreaRef
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 500);
  }, []);

  const handleSliderInteraction = useCallback((values: number[]) => {
    setIsVisible(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onValueChange(values[0]);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  }, [onValueChange]);

  useEffect(() => {
    const videoAreaElement = videoAreaRef.current;
    if (!videoAreaElement) return;

    videoAreaElement.addEventListener('mouseenter', handleMouseEnter);
    videoAreaElement.addEventListener('mouseleave', handleMouseLeave);

    // Hide slider after initial display
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 3000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      videoAreaElement.removeEventListener('mouseenter', handleMouseEnter);
      videoAreaElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseEnter, handleMouseLeave, videoAreaRef]);

  return (
    <div
      className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-[#1E293B] px-4 py-2 rounded-lg flex items-center space-x-3 shadow-lg border border-slate-700 cursor-pointer transition-opacity duration-300 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
              <Slider
                className="w-32 md:w-48"
                value={[videoSizeRatio]}
                min={0}
                max={100}
                step={1}
                onValueChange={handleSliderInteraction}
                data-larger-thumb="true"
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="15" y1="3" x2="15" y2="21"/>
              </svg>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center">
            Drag to adjust video size
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
